import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import customFetch from '@utils/customFetch';
import { useDashboardContext } from '@pages/DashboardLayout';
import { toast } from 'react-toastify';
import { ArrowLeftIcon } from '@heroicons/react/20/solid';
import { LINK_TYPE } from '@utils/constants';
import { LinksContext } from '@pages/Links';
import LinkList from '@components/dashboard/links/verwaltung/LinkList';
import LinkEditSlideIn from '@components/dashboard/links/verwaltung/LinkEditSlideIn';
import LinkDeleteModal from '@components/dashboard/links/verwaltung/LinkDeleteModal';
import ErscheinungsbildForm from '@components/dashboard/links/erscheinungsbild/ErscheinungsbildForm';

// Combined action: handles both link edit and appearance form submissions
export const action =
  (queryClient) =>
  async ({ request, params }) => {
    const formData = await request.formData();
    const data = Object.fromEntries(formData);
    const targetUserId = data.targetUserId || params.userId;
    const queryParam = targetUserId ? `?targetUserId=${targetUserId}` : '';

    // Detect if this is an appearance form (has linkColor/iconColor, no _id for link)
    if (data.linkColor !== undefined && data._id === undefined) {
      const file = formData.get('headerContent');
      if (file && file.size > 10000000) {
        toast.error('Das Bild darf nicht grösser als 10MB sein');
        return null;
      }
      try {
        await customFetch.patch(
          `/links/settings/erscheinungsbild${queryParam}`,
          formData
        );
        queryClient.invalidateQueries(['userAppearance', targetUserId]);
        queryClient.invalidateQueries(['erscheinungsbild']);
        toast.success('Erscheinungsbild angepasst!');
        return null;
      } catch (error) {
        toast.error(error?.response?.data?.msg);
        return error;
      }
    }

    // Link edit/create
    const itemId = data._id;
    delete data._id;
    delete data.targetUserId;

    if (data.new === 'true') {
      try {
        await customFetch.post(`/links${queryParam}`, data);
        queryClient.invalidateQueries(['userLinks', targetUserId]);
        queryClient.invalidateQueries(['links']);
        toast.success('Erfolgreich erstellt!');
        return null;
      } catch (error) {
        toast.error(error?.response?.data?.msg);
        return error;
      }
    } else {
      try {
        await customFetch.patch(`/links/${itemId}${queryParam}`, data);
        queryClient.invalidateQueries(['userLinks', targetUserId]);
        queryClient.invalidateQueries(['links']);
        toast.success('Erfolgreich angepasst!');
        return null;
      } catch (error) {
        toast.error(error?.response?.data?.msg);
        return error;
      }
    }
  };

const userLinksQuery = (userId) => ({
  queryKey: ['userLinks', userId],
  queryFn: async () => {
    const data = await customFetch.get(`/links?targetUserId=${userId}`);
    return data;
  },
});

const userAppearanceQuery = (userId) => ({
  queryKey: ['userAppearance', userId],
  queryFn: async () => {
    const data = await customFetch.get(
      `/links/settings/erscheinungsbild?targetUserId=${userId}`
    );
    return data;
  },
});

// Alle Mitglieder statt der ersten Seite der Benutzerliste: sonst fände die
// Detailseite ab dem 21. Mitglied niemanden mehr. Dieselbe Abfrage wie im
// Bestellformular und in der Kartenverwaltung (ein Cache-Eintrag).
const companyMembersQuery = (companyId) => ({
  queryKey: ['companyUsers', companyId, 'all'],
  queryFn: async () => {
    const { data } = await customFetch.get(`/companies/${companyId}/users`, {
      params: { limit: 200 },
    });
    return data;
  },
});

export const loader = (queryClient) => async ({ params }) => {
  const userData = await queryClient.ensureQueryData({
    queryKey: ['user'],
    queryFn: async () => {
      const { data } = await customFetch.get('/users/current-user');
      return data;
    },
  });
  const companyId = userData.user.company;
  if (!companyId) return {};

  const companyUsersData = await queryClient.ensureQueryData(
    companyMembersQuery(companyId)
  );

  await queryClient.ensureQueryData(userLinksQuery(params.userId));
  await queryClient.ensureQueryData(userAppearanceQuery(params.userId));

  const member = companyUsersData.users?.find(
    (u) => u._id === params.userId
  );

  return { companyId, member };
};

const CompanyUserDetail = () => {
  const { user } = useDashboardContext();
  const companyId = user.company;
  const { userId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState('links');
  const [saving, setSaving] = useState(false);

  // User profile state
  const [profileData, setProfileData] = useState(null);

  // Links context state
  const [linkEdit, setLinkEdit] = useState({
    title: '',
    link: '',
    type: LINK_TYPE.LINK,
  });
  const [showSlideIn, setShowSlideIn] = useState(false);
  const [editIconValue, setEditIconValue] = useState('');
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [listItems, setListItems] = useState([]);

  // Queries
  const linksData = useQuery(userLinksQuery(userId));
  const links = linksData.data;
  const appearanceData = useQuery(userAppearanceQuery(userId));

  // Load company users to get member info
  const { data: companyUsersData } = useQuery(companyMembersQuery(companyId));

  const member = companyUsersData?.users?.find((u) => u._id === userId);

  // Initialize profile data when member loads
  React.useEffect(() => {
    if (member && !profileData) {
      setProfileData({
        firstName: member.firstName || '',
        lastName: member.lastName || '',
        title: member.title || '',
        email: member.email || '',
      });
    }
  }, [member]);

  // Initialize list items when links load
  React.useEffect(() => {
    if (links?.data?.links) {
      setListItems(links.data.links);
    }
  }, [links?.data?.links]);

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await customFetch.patch(
        `/companies/${companyId}/users/${userId}`,
        profileData
      );
      queryClient.invalidateQueries(['companyUsers', companyId]);
      toast.success('Profil aktualisiert');
    } catch (error) {
      toast.error(error?.response?.data?.msg || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  if (linksData.isLoading || !member || !profileData) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-svisit-primary-600" />
      </div>
    );
  }

  const linkSettings = appearanceData?.data?.data?.linkSettings || {};

  const tabs = [
    { key: 'links', label: 'Links' },
    { key: 'erscheinungsbild', label: 'Erscheinungsbild' },
  ];

  return (
    <LinksContext.Provider
      value={{
        links,
        linkEdit,
        showSlideIn,
        setShowSlideIn,
        setLinkEdit,
        editIconValue,
        setEditIconValue,
        openDeleteModal,
        setOpenDeleteModal,
        listItems,
        setListItems,
        targetUserId: userId,
        linksQueryKey: ['userLinks', userId],
      }}
    >
      <LinkEditSlideIn />
      <LinkDeleteModal />

      <div className="bg-white px-6 py-6 sm:py-6 lg:px-8">
        {/* Back navigation */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/dashboard/firma/mitarbeiter')}
            className="flex items-center text-sm text-gray-500 hover:text-gray-700"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            Zurück zu Mitarbeiter
          </button>
        </div>

        {/* User Profile Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4">
            <img
              src={member.avatarUrl || member.avatar}
              alt="avatar"
              className="h-16 w-16 rounded-full object-cover bg-gray-200"
            />
            <div>
              <h1 className="text-xl font-semibold text-gray-900">
                {member.firstName} {member.lastName}
              </h1>
              <p className="text-sm text-gray-500">{member.email}</p>
              {member.title && (
                <p className="text-sm text-gray-400">{member.title}</p>
              )}
            </div>
          </div>
        </div>

        {/* Profile Edit Form */}
        <form onSubmit={handleSaveProfile} className="mb-8">
          <div className="bg-white shadow-xs ring-1 ring-gray-900/5 sm:rounded-lg">
            <div className="px-4 py-6 sm:p-8">
              <h2 className="text-base font-semibold leading-7 text-gray-900 mb-6">
                Profil bearbeiten
              </h2>
              <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
                <div>
                  <label
                    htmlFor="firstName"
                    className="block text-sm font-medium leading-6 text-gray-900"
                  >
                    Vorname
                  </label>
                  <div className="mt-2">
                    <input
                      type="text"
                      name="firstName"
                      id="firstName"
                      value={profileData.firstName}
                      onChange={handleProfileChange}
                      className="block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-600 sm:text-sm sm:leading-6"
                    />
                  </div>
                </div>
                <div>
                  <label
                    htmlFor="lastName"
                    className="block text-sm font-medium leading-6 text-gray-900"
                  >
                    Nachname
                  </label>
                  <div className="mt-2">
                    <input
                      type="text"
                      name="lastName"
                      id="lastName"
                      value={profileData.lastName}
                      onChange={handleProfileChange}
                      className="block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-600 sm:text-sm sm:leading-6"
                    />
                  </div>
                </div>
                <div>
                  <label
                    htmlFor="title"
                    className="block text-sm font-medium leading-6 text-gray-900"
                  >
                    Titel / Jobbezeichnung
                  </label>
                  <div className="mt-2">
                    <input
                      type="text"
                      name="title"
                      id="title"
                      value={profileData.title}
                      onChange={handleProfileChange}
                      className="block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-600 sm:text-sm sm:leading-6"
                    />
                  </div>
                </div>
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium leading-6 text-gray-900"
                  >
                    E-Mail
                  </label>
                  <div className="mt-2">
                    <input
                      type="email"
                      name="email"
                      id="email"
                      value={profileData.email}
                      onChange={handleProfileChange}
                      className="block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-600 sm:text-sm sm:leading-6"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="border-t border-gray-200 px-4 py-4 sm:px-8 flex justify-end">
              <button
                type="submit"
                disabled={saving}
                className="rounded-md bg-svisit-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-svisit-primary-500 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {saving ? 'Speichert...' : 'Profil speichern'}
              </button>
            </div>
          </div>
        </form>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium ${
                  activeTab === tab.key
                    ? 'border-svisit-primary-500 text-svisit-primary-600'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'links' && <LinkList />}
        {activeTab === 'erscheinungsbild' && (
          <ErscheinungsbildForm {...linkSettings} targetUserId={userId} />
        )}
      </div>
    </LinksContext.Provider>
  );
};

export default CompanyUserDetail;
